/**
 * Der einzige Schreibweg für Tauchgänge und Stammdaten.
 *
 * Nach dem Vorbild von `checkin.js:speichere()`: Wenn es nur eine Stelle gibt,
 * an der geschrieben wird, kann kein Satz ohne Zeitstempel und keine Löschung
 * ohne Grabstein entstehen.
 */

import { MIT_ZEITSTEMPEL, SPEICHER } from './db.js';

const STUECK = 200;

function abschluss(transaktion) {
  return new Promise((erfuellen, ablehnen) => {
    transaktion.oncomplete = () => erfuellen();
    transaktion.onerror = () => ablehnen(transaktion.error);
    transaktion.onabort = () => ablehnen(transaktion.error);
  });
}

function pruefeSpeicher(speicher) {
  if (!MIT_ZEITSTEMPEL.includes(speicher)) {
    throw new Error(`Unbekannter Speicher: ${speicher}`);
  }
}

/**
 * Kennung nach UUIDv7 — 48 Bit Zeitstempel, Rest Zufall.
 *
 * `crypto.randomUUID()` liefert v4 und ist damit unbrauchbar: Die zeitliche
 * Sortierbarkeit ist der Grund, warum CLAUDE.md Entscheid 2 auf v7 besteht.
 */
export function neueId(zeitpunkt = Date.now()) {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);

  // 48 Bit Millisekunden, big endian, in die ersten sechs Bytes.
  let rest = BigInt(zeitpunkt);
  for (let i = 5; i >= 0; i -= 1) {
    bytes[i] = Number(rest & 0xffn);
    rest >>= 8n;
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x70;   // Version 7
  bytes[8] = (bytes[8] & 0x3f) | 0x80;   // Variante RFC 4122

  const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-`
    + `${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * Sätze schreiben — einen oder viele.
 *
 * In Häppchen, damit die Oberfläche ansprechbar bleibt. Das kostet die
 * Unteilbarkeit: Bricht es beim 400. von 800 ab, sind 400 geschrieben. Für
 * Sammeländerungen ist das tragbar, weil sie wiederholbar sind und zum selben
 * Ergebnis führen.
 */
export async function speichere(db, speicher, saetze, optionen = {}) {
  pruefeSpeicher(speicher);
  const liste = Array.isArray(saetze) ? saetze : [saetze];
  const jetzt = new Date().toISOString();
  const geraet = optionen.geraet ?? null;
  const melde = optionen.melde ?? null;
  const kennungen = [];

  for (let i = 0; i < liste.length; i += STUECK) {
    try {
      const t = db.transaction(speicher, 'readwrite');
      const store = t.objectStore(speicher);
      for (const roh of liste.slice(i, i + STUECK)) {
        const satz = { ...roh, id: roh.id ?? neueId(), geaendert: jetzt };
        if (geraet) satz.geraet = geraet;
        store.put(satz);
        kennungen.push(satz.id);
      }
      await abschluss(t);
    } catch (fehler) {
      // Ein durchgereichter QuotaExceededError sagt niemandem, was zu tun ist.
      if (fehler?.name === 'QuotaExceededError') {
        throw new Error('Der Speicherplatz des Browsers ist erschöpft. '
          + `Geschrieben wurden ${kennungen.length} von ${liste.length} Sätzen. `
          + 'Zieh einen Abzug und gib im Browser Speicher frei.');
      }
      throw fehler;
    }
    if (melde) melde(Math.min(i + STUECK, liste.length), liste.length);
  }
  meldeAenderung();
  return kennungen;
}

function ergebnis(anfrage) {
  return new Promise((erfuellen, ablehnen) => {
    anfrage.onsuccess = () => erfuellen(anfrage.result);
    anfrage.onerror = () => ablehnen(anfrage.error);
  });
}

/**
 * Sätze endgültig löschen und je Satz einen Grabstein hinterlassen.
 *
 * Beides in einer Transaktion: Verschwände ein Satz ohne Merkzettel, legte ihn
 * der nächste ergänzende Import wieder an — die App kann "hatte ich nie" und
 * "habe ich gelöscht" sonst nicht unterscheiden.
 */
export async function loesche(db, speicher, ids) {
  pruefeSpeicher(speicher);
  const liste = Array.isArray(ids) ? ids : [ids];
  if (liste.length === 0) return 0;

  const jetzt = new Date().toISOString();
  const t = db.transaction([speicher, SPEICHER.grabsteine], 'readwrite');
  const store = t.objectStore(speicher);
  const merkzettel = t.objectStore(SPEICHER.grabsteine);
  for (const id of liste) {
    store.delete(id);
    merkzettel.put({ id, speicher, geloescht_am: jetzt });
  }
  await abschluss(t);
  meldeAenderung();
  return liste.length;
}

export async function grabsteine(db) {
  const t = db.transaction(SPEICHER.grabsteine, 'readonly');
  return ergebnis(t.objectStore(SPEICHER.grabsteine).getAll());
}

/** Schnelle Nachschlagehilfe für den Import, der jede Kennung einmal prüft. */
export function istGeloescht(merkzettel, id) {
  return merkzettel.some((g) => g.id === id);
}

/**
 * Wer auf Änderungen hören will, meldet sich hier an.
 *
 * `saetze.js` weiss nichts vom Abgleich — sonst zeigte die Abhängigkeit in die
 * falsche Richtung und der Schreibweg hinge am Netz.
 */
const zuhoerer = new Set();

export function beiAenderung(rueckruf) {
  zuhoerer.add(rueckruf);
  return () => zuhoerer.delete(rueckruf);
}

function meldeAenderung() {
  for (const rueckruf of zuhoerer) {
    try {
      rueckruf();
    } catch {
      // Ein kaputter Zuhörer darf das Schreiben nicht kippen — die Daten sind
      // längst in der Datenbank.
    }
  }
}
